import React from "react";
import { Button, Card, CardBody, Col, Input, Label, Row } from "reactstrap";
import Flatpickr from "react-flatpickr";
import { CatalogoItem, SaasEmpresa, SaasSucursal, SaasTalonario } from "../constants";

type InvoiceHeaderProps = {
  empresas: SaasEmpresa[];
  sucursales: SaasSucursal[];
  comprobantes: CatalogoItem[];
  condiciones: CatalogoItem[];
  talonarios: SaasTalonario[];
  empresaId: number | null;
  sucursalId: number | null;
  tipoComprobanteId: number | null;
  condicionVentaId: number | null;
  talonarioId: number | null;
  emissionDate: Date;
  dueDateLabel: string;
  showDueDateInfo: boolean;
  selectedClientLabel: string;
  onEmpresaChange: (id: number | null) => void;
  onSucursalChange: (id: number | null) => void;
  onTipoComprobanteChange: (id: number | null) => void;
  onCondicionChange: (id: number | null) => void;
  onDateChange: (value: Date) => void;
  onClientFieldFocus: () => void;
  onDueDateClick: () => void;
  onTalonarioChange: (id: number | null) => void;
};

const renderCatalogoOption = (item: CatalogoItem) =>
  item.descripcion || item.codigo || `#${item.id}`;

const renderTalonarioLabel = (talonario: SaasTalonario) => {
  const serie = talonario.serie ? `-${talonario.serie}` : "";
  return `${talonario.establecimiento}-${talonario.punto_emision}${serie} (Timbrado ${
    talonario.timbrado_numero ?? talonario.timbrado_id
  })`;
};

const InvoiceHeader: React.FC<InvoiceHeaderProps> = ({
  empresas,
  sucursales,
  comprobantes,
  condiciones,
  talonarios,
  empresaId,
  sucursalId,
  tipoComprobanteId,
  condicionVentaId,
  talonarioId,
  emissionDate,
  dueDateLabel,
  showDueDateInfo,
  selectedClientLabel,
  onEmpresaChange,
  onSucursalChange,
  onTipoComprobanteChange,
  onCondicionChange,
  onDateChange,
  onClientFieldFocus,
  onDueDateClick,
  onTalonarioChange,
}) => (
  <Card className="shadow-sm mb-4">
    <CardBody>
      <Row className="gy-3">
        <Col md={3}>
          <Label className="form-label">Empresa</Label>
          <Input
            type="select"
            value={empresaId ?? ""}
            onChange={(event) =>
              onEmpresaChange(event.target.value ? Number(event.target.value) : null)
            }
          >
            <option value="">Seleccioná una empresa</option>
            {empresas.map((empresa) => (
              <option key={empresa.id} value={empresa.id}>
                {empresa.razon_social}
              </option>
            ))}
          </Input>
        </Col>
        <Col md={3}>
          <Label className="form-label">Sucursal</Label>
          <Input
            type="select"
            value={sucursalId ?? ""}
            onChange={(event) =>
              onSucursalChange(event.target.value ? Number(event.target.value) : null)
            }
            disabled={!empresaId}
          >
            <option value="">Seleccioná una sucursal</option>
            {sucursales.map((branchOption) => (
              <option key={branchOption.id} value={branchOption.id}>
                {branchOption.descripcion} ({branchOption.codigo})
              </option>
            ))}
          </Input>
        </Col>
        <Col md={3}>
          <Label className="form-label">Tipo de comprobante</Label>
          <Input
            type="select"
            value={tipoComprobanteId ?? ""}
            onChange={(event) =>
              onTipoComprobanteChange(
                event.target.value ? Number(event.target.value) : null,
              )
            }
          >
            <option value="">Seleccioná tipo</option>
            {comprobantes.map((type) => (
              <option key={type.id} value={type.id}>
                {renderCatalogoOption(type)}
              </option>
            ))}
          </Input>
        </Col>
        <Col md={3}>
          <Label className="form-label">Condición de venta</Label>
          <Input
            type="select"
            value={condicionVentaId ?? ""}
            onChange={(event) =>
              onCondicionChange(event.target.value ? Number(event.target.value) : null)
            }
          >
            <option value="">Seleccioná condición</option>
            {condiciones.map((condition) => (
              <option key={condition.id} value={condition.id}>
                {renderCatalogoOption(condition)}
              </option>
            ))}
          </Input>
        </Col>
      </Row>
      <Row className="gy-3 mt-1">
        <Col md={2}>
          <Label className="form-label">Fecha emisión</Label>
          <Flatpickr
            className="form-control"
            value={emissionDate}
            onChange={(selected) => {
              if (selected[0]) {
                onDateChange(selected[0]);
              }
            }}
          />
        </Col>
        <Col md={4}>
          <Label className="form-label">Talonario</Label>
          <Input
            type="select"
            value={talonarioId ?? ""}
            onChange={(event) =>
              onTalonarioChange(
                event.target.value ? Number(event.target.value) : null,
              )
            }
            disabled={!talonarios.length}
          >
            <option value="">
              {talonarios.length ? "Seleccioná un talonario" : "Sin talonarios disponibles"}
            </option>
            {talonarios.map((talonario) => (
              <option key={talonario.id} value={talonario.id}>
                {renderTalonarioLabel(talonario)}
              </option>
            ))}
          </Input>
          {!talonarios.length && (
            <small className="text-muted">
              Registra un talonario para la sucursal seleccionada.
            </small>
          )}
        </Col>
        <Col md={3}>
          <Label className="form-label">Cliente</Label>
          <Input
            readOnly
            value={selectedClientLabel}
            placeholder="Haz clic para buscar"
            onClick={onClientFieldFocus}
            onFocus={onClientFieldFocus}
          />
        </Col>
        <Col md={3}>
          <Label className="form-label">Vencimiento</Label>
          <div className="border rounded p-2">
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">Fecha estimada</small>
              <span>{dueDateLabel}</span>
            </div>
            {showDueDateInfo && (
              <div className="d-flex align-items-center gap-2 mt-1">
                <Button color="link" size="sm" className="p-0" onClick={onDueDateClick}>
                  Cambiar
                </Button>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </CardBody>
  </Card>
);

export default InvoiceHeader;
